import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ExtensionCatalogueItem, OrganisationLibraryItem } from "@zworks/console-contracts";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { PageHeader } from "@/components/bespoke/page-header";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ApiError, extensionCatalogueQuery, organisationLibraryQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { InstalledPacksSection } from "./installed-packs-section";
import { kindLabels, packStatusBadges, stateBadges } from "./marketplace-labels";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A search reads at most this many catalogue pages on its own. Later pages wait for "Load more". */
export const SEARCH_PAGE_LIMIT = 5;

/**
 * The Marketplace page: what the project runs, then the published catalogue, then the
 * organisation's private library.
 *
 * Installed comes first because the question a person arrives with is usually "what am I
 * running", not "what could I run".
 */
export function MarketplacePage() {
  const { project, loaded } = useCurrentProject();
  return (
    <div className="flex flex-col gap-8">
      <PageHeader title="Marketplace" />
      {loaded && project ? <InstalledPacksSection projectId={project.id} /> : null}
      <PackDiscoverySection />
      <OrganisationLibrarySection />
    </div>
  );
}

/** The published catalogue, searchable by name, kind, and version. The console only displays it. */
function PackDiscoverySection() {
  const [query, setQuery] = useState("");
  const catalogue = useInfiniteQuery(extensionCatalogueQuery());
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = catalogue;
  const pagesLoaded = catalogue.data?.pages.length ?? 0;
  const searching = query.trim().length > 0;

  // A search reads up to SEARCH_PAGE_LIMIT pages, so a pack on a later page is still found.
  // A failed page stops the reads; "Try again" resumes them.
  useEffect(() => {
    if (!searching || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    if (pagesLoaded >= SEARCH_PAGE_LIMIT) return;
    void fetchNextPage();
  }, [
    searching,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    pagesLoaded,
    fetchNextPage,
  ]);

  return (
    <section aria-labelledby="catalogue-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SectionHeading id="catalogue-heading">Packs</SectionHeading>
        <Input
          type="search"
          aria-label="Search the marketplace"
          placeholder="Search packs, skills, and tools"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="sm:max-w-xs"
        />
      </div>
      {catalogue.isPending ? (
        <LoadingState label="Loading the catalogue" />
      ) : catalogue.isError && !catalogue.data ? (
        <CatalogueError error={catalogue.error} onRetry={() => void catalogue.refetch()} />
      ) : (
        <CatalogueList
          items={catalogue.data.pages.flatMap((page) => page.items)}
          hasMore={catalogue.hasNextPage}
          isLoadingMore={catalogue.isFetchingNextPage}
          loadMoreError={catalogue.isFetchNextPageError}
          onLoadMore={() => void catalogue.fetchNextPage()}
          query={query}
          pagesLoaded={pagesLoaded}
        />
      )}
    </section>
  );
}

/** Filters the loaded catalogue pages by name, kind, or version. */
function CatalogueList({
  items,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
  query,
  pagesLoaded,
}: {
  items: ExtensionCatalogueItem[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: boolean;
  onLoadMore: () => void;
  query: string;
  pagesLoaded: number;
}) {
  if (items.length === 0 && !hasMore) {
    return (
      <EmptyState
        title="No packs have been published yet"
        description="Reviewed packs, skills, and tools will appear here once they are approved."
      />
    );
  }
  const needle = query.trim().toLowerCase();
  const searching = needle.length > 0;
  const matches = searching
    ? items.filter((item) =>
        [item.name, kindLabels[item.kind], item.version].some((field) =>
          field.toLowerCase().includes(needle),
        ),
      )
    : items;
  const searchLimitReached = searching && hasMore && pagesLoaded >= SEARCH_PAGE_LIMIT;
  const stillSearching = searching && hasMore && !searchLimitReached && !loadMoreError;
  return (
    <div className="flex flex-col gap-3">
      <p role="status" aria-live="polite" className="sr-only">
        {searching ? `${matches.length} ${matches.length === 1 ? "pack" : "packs"} found` : ""}
      </p>
      {searchLimitReached ? (
        <p className="text-sm text-muted-foreground">
          {`Searched the first ${SEARCH_PAGE_LIMIT} pages. Load more to search the rest.`}
        </p>
      ) : null}
      {matches.length === 0 ? (
        <EmptyState
          title={
            stillSearching
              ? "Searching the catalogue"
              : hasMore
                ? "Nothing on the pages searched so far matches"
                : items.length === 0
                  ? "No packs are on this page"
                  : "No packs match that search"
          }
          description={
            stillSearching
              ? "Reading more pages for this search."
              : hasMore
                ? "Load more to search the rest of the catalogue."
                : "Try a different name, kind, or version."
          }
        />
      ) : null}
      {matches.length > 0 ? (
        <ul role="list" aria-label="Packs" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {matches.map((item) => (
            <li key={item.id}>
              <PackCard item={item} />
            </li>
          ))}
        </ul>
      ) : null}
      {hasMore ? (
        <div className="flex flex-col items-start gap-2">
          {loadMoreError ? (
            <p role="alert" className="text-sm text-destructive-text">
              More packs could not be loaded. Try again.
            </p>
          ) : null}
          <Button variant="outline" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore
              ? "Loading more packs…"
              : loadMoreError
                ? "Try again"
                : "Load more packs"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** One catalogue entry. Its name opens the pack detail. */
function PackCard({ item }: { item: ExtensionCatalogueItem }) {
  const state = stateBadges[item.state];
  const declared = item.packStatus ? packStatusBadges[item.packStatus] : null;
  const capabilities = item.capabilities.length;
  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <Link
              to={`/marketplace/packs/${item.id}`}
              aria-label={`${item.name} v${item.version}`}
              className="truncate font-medium text-foreground underline-offset-4 hover:underline"
            >
              {item.name}
            </Link>
            <p className="font-mono text-xs text-muted-foreground">v{item.version}</p>
          </div>
          <Badge variant={state.variant}>{state.label}</Badge>
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          <Badge variant="muted">{kindLabels[item.kind]}</Badge>
          {item.domain ? <Badge variant="muted">{item.domain}</Badge> : null}
          {declared ? <Badge variant={declared.variant}>{declared.label}</Badge> : null}
          <span className="text-xs text-muted-foreground">
            {capabilities} {capabilities === 1 ? "capability" : "capabilities"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/** A forbidden read says so. Any other failure offers a retry with the request identifier. */
export function CatalogueError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <ForbiddenState
        title="The catalogue is not available to you"
        description="Ask an administrator for access to the catalogue."
      />
    );
  }
  // Prefer what the server said. A fixed sentence here overrode a specific server reason: the
  // API distinguishes an unreachable runtime from a rejected console credential, and the second
  // is not fixable by retrying, which is exactly what this sentence told the reader to do.
  const detail = error instanceof ApiError ? error.problem?.detail : undefined;
  // A rejected credential is not fixed by trying again: the API answers 503 with copy that says
  // so. Offering the button beside that sentence contradicts it, and a reader who presses it
  // gets the same error. Measured on staging: the copy said "Retrying will not help" while the
  // control sent another request.
  const retryHelps = !(error instanceof ApiError && error.status === 503);
  return (
    <ErrorState
      title="The catalogue could not be loaded"
      description={detail ?? "The agent runtime did not answer. Try again shortly."}
      requestId={error instanceof ApiError ? (error.requestId ?? "unavailable") : "unavailable"}
      onRetry={retryHelps ? onRetry : undefined}
    />
  );
}

/** The library an organisation shares across its projects. The link carries the organisation. */
function OrganisationLibrarySection() {
  const [params] = useSearchParams();
  const organisationId = params.get("organisation")?.trim().toLowerCase() ?? "";
  const valid = UUID.test(organisationId);
  const library = useQuery({ ...organisationLibraryQuery(organisationId), enabled: valid });

  return (
    <section aria-labelledby="organisation-library-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <SectionHeading id="organisation-library-heading">Organisation library</SectionHeading>
        <p className="text-sm text-muted-foreground">
          Reviewed packs your organisation shares across its projects. Only its members can see
          them.
        </p>
      </div>
      {!valid ? (
        <EmptyState
          title="Choose an organisation library"
          description="Open the library link your organisation shares to see its reviewed packs."
        />
      ) : library.isPending ? (
        <LoadingState label="Loading the organisation library" />
      ) : library.isError ? (
        <LibraryError error={library.error} onRetry={() => void library.refetch()} />
      ) : library.data.items.length === 0 ? (
        <EmptyState
          title="No packs in this library yet"
          description="Packs appear here once your organisation's reviewers publish them."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <ul role="list" aria-label="Organisation packs" className="grid gap-3 sm:grid-cols-2">
            {library.data.items.map((item) => (
              <li key={item.id}>
                <LibraryCard item={item} />
              </li>
            ))}
          </ul>
          {library.data.nextCursor ? (
            <p className="text-sm text-muted-foreground">Only the first page of packs is shown.</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

function LibraryCard({ item }: { item: OrganisationLibraryItem }) {
  const state = stateBadges[item.state];
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="truncate font-medium text-foreground">{item.name}</p>
            <p className="font-mono text-xs text-muted-foreground">v{item.version}</p>
          </div>
          <Badge variant={state.variant}>{state.label}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="muted">{kindLabels[item.kind]}</Badge>
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {item.digest.slice(0, 19)}…
          </code>
        </div>
      </CardContent>
    </Card>
  );
}

/** A missing or hidden library reads the same way, so the screen never confirms it exists. */
function LibraryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <ForbiddenState
        title="This library is not available to you"
        description="Check the link, or ask an administrator of the organisation for access."
      />
    );
  }
  const detail = error instanceof ApiError ? error.problem?.detail : undefined;
  return (
    <ErrorState
      title="The library could not be loaded"
      description={detail ?? "The agent runtime did not answer. Try again shortly."}
      requestId={error instanceof ApiError ? (error.requestId ?? "unavailable") : "unavailable"}
      onRetry={onRetry}
    />
  );
}
